import { type ReactNode } from "react";

import {
  RadioGroup as ReactAriaRadioGroup,
  type RadioGroupProps as ReactAriaRadioGroupProps,
} from "react-aria-components";

import { FormFieldAssistiveText } from "#components/v2/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/v2/shared/FormFieldHeader";
import { cn } from "#utils";

export interface RadioGroupProps extends ReactAriaRadioGroupProps {
  /** Label text for the radio group */
  label?: string;
  /** Assistive text displayed below the group */
  assistiveText?: ReactNode;
  /** Tooltip text displayed next to the label */
  tooltipText?: ReactNode;
  /** Additional class name for the group container */
  className?: string;
  /** Layout orientation for the radio buttons */
  orientation?: "horizontal" | "vertical";
}

export function RadioGroup({
  label,
  assistiveText,
  tooltipText,
  className,
  isDisabled,
  isInvalid,
  orientation = "vertical",
  children,
  ...props
}: RadioGroupProps) {
  return (
    <ReactAriaRadioGroup
      className={cn("flex flex-col gap-[.625rem]", className)}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      orientation={orientation}
      {...props}
    >
      {/*
        Using render props pattern to access group state (isDisabled, isInvalid, etc.)
        from React Aria. This allows us to style child elements based on group state
        without prop drilling or additional context.
      */}
      {(renderProps) => (
        <>
          <FormFieldHeader
            isDisabled={renderProps.isDisabled}
            label={label}
            tooltipText={tooltipText}
          />
          <div
            className={cn(
              "flex gap-xl",
              orientation === "horizontal" ? "flex-row flex-wrap" : "flex-col",
            )}
          >
            {/*
              Support both static children and render prop pattern.
              Static: <RadioGroup><Radio /></RadioGroup>
              Render prop: <RadioGroup>{(state) => <Radio />}</RadioGroup>
            */}
            {typeof children === "function" ? children(renderProps) : children}
          </div>
          <FormFieldAssistiveText
            isDisabled={renderProps.isDisabled}
            isInvalid={renderProps.isInvalid}
          >
            {assistiveText}
          </FormFieldAssistiveText>
        </>
      )}
    </ReactAriaRadioGroup>
  );
}
